import IPhysicalAnalysis from 'types/model/IPhysicalAnalysis';
import { action, computed, observable } from 'mobx';

import {
  HIDDEN_FIELDS,
  STEP_COMPLETED,
  STEP_DEFECTS,
  STEP_DRAFT,
} from './physicalAnalysisSteps';

// Un 0 no es "sin valor". Con `values.x || ''` el cero se convertia en vacio, y el campo
// se reenviaba en blanco: el formulario reclamaba el dato Y decia que no se podia
// modificar, todo junto. Le pegaba a `density`, que al ser una columna integer llega
// como el numero 0 (falsy); las demas son decimal y llegan como "0.0" (string, truthy).
const initialValue = (value: null | string | number | undefined): string =>
  value === null || value === undefined ? '' : String(value);

const valuePercentage = (value: string, green: string): string => {
  const procent = ((parseFloat(value) * 100) / parseFloat(green)).toFixed(2);

  if (green && procent && procent !== 'NaN' && value) {
    return procent;
  }

  return '';
};

class PhysicalAnalysisStore {
  private exportSize: number;
  public sumTolerance: number;

  @observable public values = [];
  @observable public isValid = true;
  @observable public sampleWeight = '300'; // @TODO: wire this
  @observable public green: string;
  @observable public sieve0: string;
  @observable public sieve12: string;
  @observable public sieve14: string;
  @observable public sieve15: string;
  @observable public group1: string;
  @observable public group2: string;
  @observable public broca: string;
  @observable public paloteo: string;
  @observable public ripio: string;
  @observable public guayabas: string;
  @observable public caracol: string;
  @observable public density: string;
  @observable public observations: string;
  @observable public rejectReason: string;
  @observable public acceptingReasonId;

  // PLA-1077: paso confirmado del asistente. null = fuera del asistente (muestras
  // sueltas, clones e historico): todo el store se comporta como antes del ticket.
  @observable public step: null | number;

  constructor(values: IPhysicalAnalysis, exportSize: number, sumTolerance: number) {
    this.sampleWeight = '300'; // @TODO: wire this
    this.exportSize = exportSize;
    this.sumTolerance = sumTolerance;
    this.step = values.step === undefined ? null : values.step;
    this.green = initialValue(values.green);
    this.sieve0 = initialValue(values.sieve_0);
    this.sieve12 = initialValue(values.sieve_12);
    this.sieve14 = initialValue(values.sieve_14);
    this.sieve15 = initialValue(values.sieve_15);
    this.group1 = initialValue(values.group_1);
    this.group2 = initialValue(values.group_2);
    this.broca = initialValue(values.broca);
    this.paloteo = initialValue(values.paloteo);
    this.ripio = initialValue(values.ripio);
    this.guayabas = initialValue(values.guayabas);
    this.caracol = initialValue(values.caracol);
    this.density = initialValue(values.density);
    this.observations = initialValue(values.observations);
    this.rejectReason = initialValue(values.reject_reason);
    this.acceptingReasonId = values.accepting_reason_id;

    // PLA-1077 v1.1: mientras el paso 2 no este confirmado el servidor guarda ripio y
    // caracol en 0 (PhysicalAnalysis#zero_hidden_fields). Se replica aca para que la suma
    // contra la almendra que ve el usuario sea la misma que valida el servidor.
    if (this.isWizard && (this.step ?? STEP_DRAFT) < STEP_DEFECTS) {
      this.ripio = '0';
      this.caracol = '0';
    }
  }

  // #################
  // Setter for values
  // #################
  @action public setIsValid = (status: boolean) => (this.isValid = status);

  @action public setGreen = (value: string) => (this.green = value);

  @action public setSieve0 = (value: string) => (this.sieve0 = value);

  @action public setSieve12 = (value: string) => (this.sieve12 = value);

  @action public setSieve14 = (value: string) => (this.sieve14 = value);

  @action public setSieve15 = (value: string) => (this.sieve15 = value);

  @action public setGroup1 = (value: string) => (this.group1 = value);

  @action public setGroup2 = (value: string) => (this.group2 = value);

  @action public setBroca = (value: string) => (this.broca = value);

  @action public setPaloteo = (value: string) => (this.paloteo = value);

  @action public setRipio = (value: string) => (this.ripio = value);

  @action public setGuayabas = (value: string) => (this.guayabas = value);

  @action public setCaracol = (value: string) => (this.caracol = value);

  @action public setObservations = (value: string) => (this.observations = value);

  @action public setDensity = (value: string) => (this.density = value);

  @action public setStep = (value: null | number) => (this.step = value);

  // #################
  // Asistente (PLA-1077)
  // #################

  public get isWizard(): boolean {
    return this.step !== null && this.step !== undefined;
  }

  // PLA-1077 v1.1: ripio y caracol no se muestran en el asistente. Excepcion: un borrador
  // que confirmo el paso 2 antes de este cambio con valores distintos de 0 los conserva, y
  // se muestran (en solo lectura) para que la suma cuadre a la vista.
  public isHiddenField(field: string): boolean {
    if (!this.isWizard || !HIDDEN_FIELDS.includes(field)) {
      return false;
    }

    return !(parseFloat(this[field]) > 0);
  }

  // Paso que el usuario esta completando ahora: el siguiente al ultimo confirmado.
  @computed get currentStep(): number {
    if (!this.isWizard) {
      return STEP_COMPLETED;
    }

    return Math.min((this.step ?? STEP_DRAFT) + 1, STEP_COMPLETED);
  }

  // Analisis ya cerrado: se entra solo a completar las observaciones. No queda nada por
  // congelar, asi que no corresponde volver a pedir la confirmacion del paso.
  @computed get isClosed(): boolean {
    return this.isWizard && this.step === STEP_COMPLETED;
  }

  // Nota 10: los calculados recien aparecen en la pantalla de cierre, o sea DESPUES de
  // confirmar las mallas. Mientras el analista teclea no tiene que ver el factor: si lo
  // viera podria ajustar los gramos hasta que el numero le guste, que es el fraude que
  // cierra el ticket.
  @computed get showsCalculatedValues(): boolean {
    return !this.isWizard || this.currentStep === STEP_COMPLETED;
  }

  // Camino legacy (muestras sueltas): tolerancia de `standards`, como siempre. Dentro
  // del asistente la banda sale de la tabla de parametros y la evalua el componente.
  @computed get sumMatchesGreen(): boolean {
    const sum = parseFloat(this.sieveSum);
    const green = parseFloat(this.green);

    if (isNaN(sum) || isNaN(green)) {
      return false;
    }

    return sum >= green * (1 - this.sumTolerance / 100) && sum <= green;
  }

  // Nota 3: no existe un cafe sin defectos.
  @computed get defectsTotal(): number {
    return [
      this.group1,
      this.group2,
      this.broca,
      this.paloteo,
      this.ripio,
      this.guayabas,
      this.caracol,
    ].reduce((total, current) => total + (parseFloat(current) || 0), 0);
  }

  // #################
  // Calculated Values
  // #################
  @computed get mermaPercentage(): string {
    if (this.green) {
      const sampleWeight = parseFloat(this.sampleWeight);
      return (((sampleWeight - parseFloat(this.green)) / sampleWeight) * 100).toFixed(2);
    }
    return '';
  }

  @computed get sieve0Percentage(): string {
    return valuePercentage(this.sieve0, this.green);
  }

  @computed get sieve12Percentage(): string {
    return valuePercentage(this.sieve12, this.green);
  }

  @computed get sieve14Percentage(): string {
    return valuePercentage(this.sieve14, this.green);
  }

  @computed get sieve15Percentage(): string {
    return valuePercentage(this.sieve15, this.green);
  }

  @computed get sieveSum(): string {
    const sum = [
      this.group1,
      this.group2,
      this.broca,
      this.paloteo,
      this.caracol,
      this.guayabas,
      this.ripio,
      this.sieve0,
      this.sieve12,
      this.sieve14,
      this.sieve15,
    ].reduce((totalValue, currentValue) => {
      const addValue = parseFloat(currentValue) || 0;
      return totalValue + addValue;
    }, 0);

    return sum.toFixed(2);
  }

  @computed get factor14(): string {
    if (!this.showsCalculatedValues) {
      return '';
    }

    if (this.sieve14 && this.sieve15) {
      return (
        (this.exportSize * parseFloat(this.sampleWeight)) /
        (parseFloat(this.sieve14) + parseFloat(this.sieve15))
      ).toFixed(2);
    }
    return '';
  }

  @computed get factor15(): string {
    if (!this.showsCalculatedValues) {
      return '';
    }

    if (this.sieve15) {
      return (
        (this.exportSize * parseFloat(this.sampleWeight)) /
        parseFloat(this.sieve15)
      ).toFixed(2);
    }
    return '';
  }

  @computed get yield14(): string {
    if (this.factor14) {
      return (this.exportSize / parseFloat(this.factor14)).toFixed(2);
    }
    return '';
  }

  @computed get yield15(): string {
    if (this.factor15) {
      return (this.exportSize / parseFloat(this.factor15)).toFixed(4);
    }
    return '';
  }

  @computed get group1Percentage(): string {
    return valuePercentage(this.group1, this.green);
  }
  @computed get group2Percentage(): string {
    return valuePercentage(this.group2, this.green);
  }
  @computed get brocaPercentage(): string {
    return valuePercentage(this.broca, this.green);
  }
  @computed get paloteoPercentage(): string {
    return valuePercentage(this.paloteo, this.green);
  }
  @computed get ripioPercentage(): string {
    return valuePercentage(this.ripio, this.green);
  }
  @computed get guayabasPercentage(): string {
    return valuePercentage(this.guayabas, this.green);
  }
  @computed get caracolPercentage(): string {
    return valuePercentage(this.caracol, this.green);
  }
}
export default PhysicalAnalysisStore;
